import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentBase } from '../../../core/_infrastructure/classes/componentBase';
import { ExceptionManager } from '../../../core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from '../../../core/_infrastructure/classes/services/auth.service';
import { GlobalService } from '../../../core/_infrastructure/classes/services/global.service';
import { NotificationService } from '../../../core/_infrastructure/classes/services/notification.service';
import { EnumSiNoTodos, Tc } from '../../../core/_infrastructure/types/common';
import { RegExportadorViewModel } from './exportador.types';
import { ExportadorService } from './exportador.service';
import { CustomValidator } from '../../../core/_infrastructure/classes/customValidator';
import { SelectItem } from 'primeng/api';

@Component({
    selector: 'app-exportador-edit',
    templateUrl: './exportador-edit.component.html',
})
export class ExportadorEditComponent extends ComponentBase implements OnInit {
    /** exportador-edit ctor */

    editTitle: string = 'Nuevo';
    editMode: boolean = false;
    idPaisEspania: string;
    listSiNoTodos: SelectItem[];
    listPais: Tc[];
    listProvincia: Tc[];
    listMunicipio: Tc[];
    exportador: RegExportadorViewModel;
    visibilitySpinner: boolean = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private fb: UntypedFormBuilder,
        private globalService: GlobalService,
        protected notificationService: NotificationService,
        private exportadorService: ExportadorService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
        this.exportador = new RegExportadorViewModel();
    }

    ngOnInit() {
        this.globalService.getAllPaises().subscribe(res => {
            this.listPais = res;
        });

        this.createForm();

        let id = +this.activatedRoute.snapshot.params['id'];

        if (id) {
            this.editMode = true;
            this.editTitle = 'Editar';

            this.exportadorService.getExportador(id).subscribe(result => {
                this.exportador = result;

                this.updateForm();

                if (!this.isEspania()) {
                    this.form.controls['IdProvincia'].disable();
                    this.form.controls['IdMunicipio'].disable();
                } else {
                    this.form.controls['IdProvincia'].enable();
                    this.form.controls['IdMunicipio'].enable();
                }
            });
        }

        //p-selectButton
        this.listSiNoTodos = [
            { label: 'Sí', value: EnumSiNoTodos.SI },
            { label: 'NO', value: EnumSiNoTodos.NO },
            { label: 'TODOS', value: EnumSiNoTodos.TODOS },
        ];
    }

    createForm() {
        this.form = this.fb.group({
            NIF: ['', Validators.compose([Validators.required, CustomValidator.NIFValidator])],
            RazonSocial: ['', Validators.required],
            Direccion: ['', Validators.required],
            IdPais: [null, Validators.required],
            IdProvincia: [null],
            IdMunicipio: [null],
            CodigoZip: ['', Validators.required],
            Activo: EnumSiNoTodos.SI,
            FechaAlta: null,
            FechaBaja: null,
            FechaCaducidadBaja: null,
            FechaUltimaExportacion: null,
            Observaciones: '',
        });
    }

    updateForm() {
        if (this.exportador.IdPais.toString() == this.idPaisEspania) {
            this.globalService.getProvincias().subscribe(res => {
                this.listProvincia = res;
            });
            this.globalService.getMunicipios(this.exportador.IdProvincia).subscribe(res => {
                this.listMunicipio = res;
            });
        }

        this.form.setValue({
            NIF: this.exportador.NIF,
            RazonSocial: this.exportador.RazonSocial,
            Direccion: this.exportador.Direccion,
            IdPais: this.exportador.IdPais,
            IdProvincia: this.exportador.IdProvincia,
            IdMunicipio: this.exportador.IdMunicipio,
            CodigoZip: this.exportador.CodigoZip,
            Activo: this.exportador.Activo,
            FechaAlta: new Date(this.exportador.FechaAlta),
            FechaBaja: this.exportador.FechaBaja != null ? new Date(this.exportador.FechaBaja) : '',
            FechaCaducidadBaja: this.exportador.FechaCaducidadBaja != null ? new Date(this.exportador.FechaCaducidadBaja) : '',
            FechaUltimaExportacion: this.exportador.FechaUltimaExportacion != null ? new Date(this.exportador.FechaUltimaExportacion) : '',
            Observaciones: this.exportador.Observaciones,
        });
    }

    onSelectPais() {
        if (this.isEspania()) {
            this.form.controls['IdProvincia'].enable();
            this.globalService.getProvincias().subscribe(res => {
                this.listProvincia = res;
            });
            this.addValidator('IdProvincia', Validators.required);
        } else {
            this.listProvincia = [];
            this.listMunicipio = [];
        }

        this.form.patchValue({
            IdProvincia: null,
            IdMunicipio: null,
        });
    }

    onSelectProvincia() {
        const idProvincia = this.form.value.IdProvincia;

        this.form.controls['IdMunicipio'].enable();

        this.form.patchValue({
            IdMunicipio: null,
        });

        this.globalService.getMunicipios(idProvincia).subscribe(res => {
            this.listMunicipio = res;
        });

        this.addValidator('IdMunicipio', Validators.required);
    }

    isEspania() {
        let config = this.getConfig();

        this.idPaisEspania = config.IdPaisEspania;

        return this.form.value.IdPais == config.IdPaisEspania;
    }

    onSubmit() {
        this.submitted = true;

        if (!this.form.valid) {
            this.addErrorMessage('Revise los datos');
            return;
        }

        this.visibilitySpinner = true;

        let exportador = new RegExportadorViewModel();

        exportador.NIF = this.form.value.NIF;
        exportador.RazonSocial = this.form.value.RazonSocial;
        exportador.Direccion = this.form.value.Direccion;
        exportador.IdPais = this.form.value.IdPais;
        exportador.IdProvincia = this.form.value.IdProvincia;
        exportador.IdMunicipio = this.form.value.IdMunicipio;
        exportador.CodigoZip = this.form.value.CodigoZip;
        exportador.Activo = this.form.value.Activo;
        exportador.FechaAlta = this.form.value.FechaAlta;
        exportador.FechaBaja = this.form.value.FechaBaja;
        exportador.FechaCaducidadBaja = this.form.value.FechaCaducidadBaja;
        exportador.FechaUltimaExportacion = this.form.value.FechaUltimaExportacion;
        exportador.Observaciones = this.form.value.Observaciones;

        exportador.Id = this.exportador.Id;

        if (this.editMode) {
            this.exportadorService.editExportador(exportador).subscribe({
                next: () => {
                    this.addSuccessMessage('Exportador modificado correctamente');
                    this.visibilitySpinner = false;
                    this.onBack();
                },
                error: error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                },
            });
        } else {
            this.exportadorService.createExportador(exportador).subscribe({
                next: () => {
                    this.addSuccessMessage('Exportador añadido correctamente');
                    this.visibilitySpinner = false;
                    this.onBack();
                },
                error: error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                },
            });
        }
    }

    onBack() {
        this.router.navigate(['/mantenimiento/registros/exportadores']);
    }
}
